import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HiX, HiCheck, HiChevronLeft, HiArrowRight } from "react-icons/hi";
import { useAuth } from "../../../contexts/AuthContext";
import { useSubscription } from "../../../providers/SubscriptionProvider";
import { PerformanceLog, PerformanceType, TrainingLog } from "../../../types/post";
import { Sport, TrainingType } from "../../../types/activity";
import { CustomTrainingType } from "../../../types/user";
import { PeakEvent, eventDayIndex, eventLastDay } from "../../../types/event";
import { AddPerformanceResult, PerformanceDraft } from "../../../types/performance";
import { getUserPublicData } from "../../../services/userService";
import { getAllEvents, getLinkedSessions } from "../../../services/eventService";
import { activityService } from "../../../services/activityService";
import {
  getAllPerformanceLogs,
  getDayPostReferences,
  getResultsForTrainingLog,
  getTrainingLogs,
} from "../../../services/dayPostService";
import { addPerformance } from "../../../services/performanceService";
import {
  trainingTypeService,
  performanceTrainingTypeName,
} from "../../../services/trainingTypeService";
import {
  calcLoad,
  calcLoadModifier,
  calcRpe,
  fetchTrainingTypesForUser,
  parseDurationToMinutes,
} from "../training-log/trainingLogUtils";
import { useActivityLibrary } from "./useActivityLibrary";
import IntentStep from "./IntentStep";
import LinkStep from "./LinkStep";
import ResultsStep from "./ResultsStep";
import LoadStep, { LoadState, initialLoadState } from "./LoadStep";
import CompleteStep from "./CompleteStep";
import {
  EventChoice,
  PerformanceFlowSeed,
  ResultRowState,
  eventsSpanningDate,
  rowFromResult,
  rowIsEmpty,
  rowIsValid,
  rowToResultDraft,
  rowsFromEventSessions,
} from "./performanceDraft";
import { addDaysYMD } from "../../../types/event";

type StepId = "intent" | "link" | "results" | "load" | "complete";
const ALL_STEPS: StepId[] = ["intent", "link", "results", "load", "complete"];

export interface PerformanceFlowProps {
  open: boolean;
  onClose: () => void;
  /** YYYYMMDD the performance is filed under. */
  dateID: string;
  onSaved?: (result: AddPerformanceResult) => void | Promise<void>;
  /** Entry-point seed (PerformanceLog.md §3.1). */
  seed?: PerformanceFlowSeed;
  /** Edit an existing performance training log (+ its results). */
  editMode?: boolean;
  initialTrainingLog?: TrainingLog & { id: string };
  overlayZIndexClass?: string;
}

const dateIDToInputValue = (id: string): string =>
  id && id.length >= 8
    ? `${id.substring(0, 4)}-${id.substring(4, 6)}-${id.substring(6, 8)}`
    : "";
const inputValueToDateID = (val: string): string => val.replace(/-/g, "");
const formatDateID = (id: string): string => {
  const iso = dateIDToInputValue(id);
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/**
 * The five-step Performance flow (PerformanceLog.md §3) in the same
 * full-screen shell as TrainingLogForm. Writes via addPerformance (§4).
 */
const PerformanceFlow: React.FC<PerformanceFlowProps> = ({
  open,
  onClose,
  dateID: initialDateID,
  onSaved,
  seed,
  editMode = false,
  initialTrainingLog,
  overlayZIndexClass = "z-[10000]",
}) => {
  const { currentUser, userData } = useAuth();
  const { isPremium } = useSubscription();
  const uid = currentUser?.uid;

  // ── Working date ─────────────────────────────────────────────────────────
  const [dateID, setDateID] = useState(initialDateID);
  const dateInputRef = useRef<HTMLInputElement>(null);

  // ── Flow state ──────────────────────────────────────────────────────────
  const [step, setStep] = useState<StepId>("intent");
  const [performanceType, setPerformanceType] = useState<PerformanceType | null>(
    null,
  );
  const [eventChoice, setEventChoice] = useState<EventChoice>({ kind: "none" });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [upgradeLogId, setUpgradeLogId] = useState<string | null>(null);
  const [rows, setRows] = useState<ResultRowState[]>([]);
  const [loadState, setLoadState] = useState<LoadState>(initialLoadState());
  const [durationError, setDurationError] = useState("");
  const [selectedTrainingTypeId, setSelectedTrainingTypeId] = useState<
    string | null
  >(null);
  const [sportFilter, setSportFilter] = useState("all");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const initialResultIdsRef = useRef<string[]>([]);
  const [loadingSeed, setLoadingSeed] = useState(false);

  // ── Reference data ──────────────────────────────────────────────────────
  const [events, setEvents] = useState<PeakEvent[]>([]);
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);
  const [sports, setSports] = useState<Sport[]>([]);
  const [fixedTypes, setFixedTypes] = useState<
    Partial<Record<PerformanceType, TrainingType | null>>
  >({});
  const [dayTrainingLogs, setDayTrainingLogs] = useState<
    (TrainingLog & { id: string })[]
  >([]);
  const {
    activityTypes,
    loading: loadingActivityTypes,
    reload: reloadActivities,
  } = useActivityLibrary(open);

  // PB Predictor history (Premium+). Ready flag prevents false "first PB" while loading.
  const [pastPerformanceLogs, setPastPerformanceLogs] = useState<
    (PerformanceLog & { id: string })[]
  >([]);
  const [historyReady, setHistoryReady] = useState(false);
  const [historyLoadFailed, setHistoryLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchHistory = async () => {
      if (!open || !uid || !isPremium) {
        setPastPerformanceLogs([]);
        setHistoryReady(false);
        setHistoryLoadFailed(false);
        return;
      }
      setHistoryReady(false);
      setHistoryLoadFailed(false);
      try {
        const logs = await getAllPerformanceLogs(uid);
        if (cancelled) return;
        setPastPerformanceLogs(logs);
        setHistoryLoadFailed(false);
        setHistoryReady(true);
      } catch (error) {
        console.error("Error fetching performance log history:", error);
        if (cancelled) return;
        setPastPerformanceLogs([]);
        setHistoryLoadFailed(true);
        setHistoryReady(true);
      }
    };
    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, [open, uid, isPremium]);

  // Profile (sports, custom types) + catalogue + the athlete's events.
  useEffect(() => {
    if (!open || !uid) return;
    let cancelled = false;
    (async () => {
      try {
        const profile = await getUserPublicData(uid);
        if (cancelled) return;
        const sportIDs =
          profile?.trainingSportIDs ?? userData?.trainingSportIDs ?? [];
        const customs: CustomTrainingType[] | undefined =
          profile?.customTrainingTypes ?? userData?.customTrainingTypes;
        const [types, allSports, comp, game, allEvents] = await Promise.all([
          fetchTrainingTypesForUser(sportIDs, customs),
          trainingTypeService.fetchSports(),
          trainingTypeService.resolvePerformanceTrainingType("competition"),
          trainingTypeService.resolvePerformanceTrainingType("gameDay"),
          getAllEvents(uid).catch(() => [] as PeakEvent[]),
        ]);
        if (cancelled) return;
        setTrainingTypes(types);
        setSports(
          allSports
            .filter((s) => !!s.id && sportIDs.includes(s.id))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
        setFixedTypes({ competition: comp, gameDay: game });
        setEvents(allEvents);
        setEventsLoaded(true);
      } catch (error) {
        console.error("Error loading performance flow data:", error);
        setEventsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, uid]);

  // The day's plain training logs that can be upgraded to a performance.
  useEffect(() => {
    if (!open || !uid) return;
    let cancelled = false;
    (async () => {
      try {
        const refs = await getDayPostReferences(uid, dateID);
        const logs = await getTrainingLogs(uid, refs?.trainingLogIDs ?? []);
        if (cancelled) return;
        setDayTrainingLogs(
          editMode ? [] : logs.filter((l) => !l.performanceType),
        );
      } catch (error) {
        if (!cancelled) setDayTrainingLogs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, uid, dateID, editMode]);

  // ── Derived ─────────────────────────────────────────────────────────────
  const activeEvents = useMemo(
    () => events.filter((e) => e.status !== "withdrawn"),
    [events],
  );
  const spanningEvents = useMemo(
    () => eventsSpanningDate(activeEvents, dateID),
    [activeEvents, dateID],
  );
  // "Different event" pool: upcoming and recent (last 90 days), nearest first.
  const pickerEvents = useMemo(() => {
    const floor = addDaysYMD(dateID, -90);
    const distance = (e: PeakEvent) =>
      Math.abs(Number(e.date) - Number(dateID));
    return activeEvents
      .filter((e) => eventLastDay(e) >= floor)
      .sort((a, b) => distance(a) - distance(b))
      .slice(0, 40);
  }, [activeEvents, dateID]);

  const seededFromEvent = !!seed?.eventId && !editMode;
  const visibleSteps = useMemo(
    () => ALL_STEPS.filter((s) => s !== "intent" || !seededFromEvent),
    [seededFromEvent],
  );
  const stepIndex = visibleSteps.indexOf(step);

  const linkedEventId =
    eventChoice.kind === "event" ? eventChoice.eventId : undefined;
  const linkedEvent = linkedEventId
    ? events.find((e) => e.id === linkedEventId)
    : undefined;
  const existingTrainingLogId = editMode
    ? initialTrainingLog?.id
    : (upgradeLogId ?? undefined);

  const fixedTrainingType =
    performanceType && performanceType !== "testing"
      ? (fixedTypes[performanceType] ?? null)
      : null;
  const selectedTrainingType =
    performanceType === "testing"
      ? (trainingTypes.find((t) => t.id === selectedTrainingTypeId) ?? null)
      : fixedTrainingType;

  const rpe = calcRpe(loadState.muscular, loadState.respiratory);
  const durationMinutes = parseDurationToMinutes(loadState.duration);
  const load = calcLoad(
    rpe,
    durationMinutes,
    calcLoadModifier(selectedTrainingType),
  );

  const eventName =
    linkedEvent?.name ??
    (eventChoice.kind === "create" ? eventChoice.name.trim() : "");

  const resolveActivityName = useCallback(
    (activityTypeID: string, fallback?: string) =>
      activityTypes.find(
        (a) => a.id === activityTypeID || a.id?.endsWith(`_${activityTypeID}`),
      )?.name ??
      fallback ??
      "Activity",
    [activityTypes],
  );

  const trainingLogLabel = (log: TrainingLog & { id: string }) => {
    const type =
      trainingTypes.find((t) => t.id === log.trainingTypeID)?.name ??
      log.tag?.[0] ??
      "Training";
    return `${type} · ${log.duration} min · load ${log.load}`;
  };

  // ── Seeding ─────────────────────────────────────────────────────────────
  const resetState = useCallback(() => {
    setStep("intent");
    setPerformanceType(null);
    setEventChoice({ kind: "none" });
    setPickerOpen(false);
    setCreateOpen(false);
    setCreateName("");
    setUpgradeLogId(null);
    setRows([]);
    setLoadState(initialLoadState());
    setDurationError("");
    setSelectedTrainingTypeId(null);
    setSportFilter("all");
    setSubmitError(null);
    setEventsLoaded(false);
    initialResultIdsRef.current = [];
  }, []);

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      resetState();
      setDateID(
        editMode && initialTrainingLog?.dateID
          ? initialTrainingLog.dateID
          : initialDateID,
      );
      if (seed?.performanceType) setPerformanceType(seed.performanceType);
      if (editMode && initialTrainingLog) {
        setPerformanceType(initialTrainingLog.performanceType ?? "testing");
        if (initialTrainingLog.eventId) {
          setEventChoice({ kind: "event", eventId: initialTrainingLog.eventId });
        }
        setLoadState({
          ...initialLoadState(),
          duration: String(initialTrainingLog.duration ?? ""),
          muscular: initialTrainingLog.muscular ?? 1,
          respiratory: initialTrainingLog.respiratory ?? 1,
          trainingPerformed: initialTrainingLog.trainingPerformed ?? 1,
          notes: initialTrainingLog.notes ?? "",
          pain: initialTrainingLog.pain ?? false,
          injured: initialTrainingLog.injured ?? false,
        });
        setSelectedTrainingTypeId(initialTrainingLog.trainingTypeID ?? null);
      }
    }
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Edit mode: load the existing results.
  useEffect(() => {
    if (!open || !editMode || !uid || !initialTrainingLog) return;
    let cancelled = false;
    (async () => {
      setLoadingSeed(true);
      try {
        const results = await getResultsForTrainingLog(uid, initialTrainingLog.id);
        if (cancelled) return;
        initialResultIdsRef.current = results.map((r) => r.id);
        setRows(results.map((r) => rowFromResult(r)));
      } finally {
        if (!cancelled) setLoadingSeed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editMode, uid, initialTrainingLog?.id]);

  // Rows seeded from event targets must belong to the latest event choice.
  const eventSeedTokenRef = useRef(0);

  /**
   * Apply the event half of the step-2 choice. Dropping the previous event's
   * seeded rows (rows with targets) and seeding new ones from the target
   * values of sessions linked to the event.
   */
  const applyEventChoice = (next: EventChoice) => {
    setEventChoice(next);
    setPickerOpen(next.kind === "event" ? pickerOpen : false);
    setCreateOpen(next.kind === "create");
    const token = ++eventSeedTokenRef.current;
    // Keep measured rows and anything the athlete typed into; drop untouched
    // rows that were seeded from a previous event's targets.
    const keep = (r: ResultRowState) =>
      rowIsValid(r) || (!r.targets && !rowIsEmpty(r));
    setRows((prev) => prev.filter(keep));
    if (next.kind !== "event" || !uid) return;
    const event = events.find((e) => e.id === next.eventId);
    if (event && !performanceType) setPerformanceType(event.type);
    (async () => {
      try {
        const sessions = await getLinkedSessions(uid, next.eventId);
        if (eventSeedTokenRef.current !== token) return;
        setRows((prev) => {
          const kept = prev.filter(keep);
          return [
            ...kept,
            ...rowsFromEventSessions(sessions, resolveActivityName, kept),
          ];
        });
      } catch (error) {
        console.error("Error loading event sessions:", error);
      }
    })();
  };

  // Opened from an event: skip Intent (event.type) and link the event.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      return;
    }
    if (seededRef.current || editMode || !seed) return;
    if (seed.eventId) {
      if (!eventsLoaded) return; // wait for the events
      const event = events.find((e) => e.id === seed.eventId);
      seededRef.current = true;
      if (!event) {
        setStep("intent");
        return;
      }
      setPerformanceType(event.type);
      applyEventChoice({ kind: "event", eventId: event.id });
      setStep("link");
      return;
    }
    seededRef.current = true;
    if (seed.startStep !== undefined) setStep(ALL_STEPS[seed.startStep] ?? "intent");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seed, events, eventsLoaded, editMode]);

  /** Upgrade an existing plain training log of the day (keeps its load). */
  const applyUpgrade = (logId: string | null) => {
    setUpgradeLogId(logId);
    if (!logId) return;
    const log = dayTrainingLogs.find((l) => l.id === logId);
    if (!log) return;
    setLoadState((prev) => ({
      ...prev,
      duration: String(log.duration ?? ""),
      muscular: log.muscular ?? 1,
      respiratory: log.respiratory ?? 1,
      trainingPerformed: log.trainingPerformed ?? 1,
      notes: log.notes ?? prev.notes,
      pain: log.pain ?? false,
      injured: log.injured ?? false,
    }));
    if (log.trainingTypeID) setSelectedTrainingTypeId(log.trainingTypeID);
  };

  // ── Navigation ──────────────────────────────────────────────────────────
  const validateDuration = (): boolean => {
    if (!loadState.duration.trim()) {
      setDurationError("Duration is required");
      return false;
    }
    if (durationMinutes <= 0) {
      setDurationError("Enter a valid duration (e.g. 60 or 1:30)");
      return false;
    }
    setDurationError("");
    return true;
  };

  const incompleteRow = rows.find((r) => !rowIsEmpty(r) && !rowIsValid(r));

  const canAdvance = (() => {
    switch (step) {
      case "intent":
        return !!performanceType;
      case "link":
        return !createOpen || createName.trim().length > 0;
      case "results":
        return !incompleteRow;
      case "load":
        return durationMinutes > 0;
      default:
        return true;
    }
  })();

  const goNext = () => {
    if (step === "load" && !validateDuration()) return;
    if (step === "results" && incompleteRow) {
      setSubmitError("Each result needs an activity and at least one value.");
      return;
    }
    if (step === "link") {
      // Commit the typed quick-create name as the event choice.
      if (createOpen && createName.trim()) {
        setEventChoice({ kind: "create", name: createName.trim() });
      }
    }
    setSubmitError(null);
    const next = visibleSteps[stepIndex + 1];
    if (next) setStep(next);
  };
  const goBack = () => {
    const prev = visibleSteps[stepIndex - 1];
    if (prev) setStep(prev);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // ── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!uid || !performanceType) return;
    if (!validateDuration()) {
      setStep("load");
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const typeName = performanceTrainingTypeName(performanceType);
      const draft: PerformanceDraft = {
        // Linked events carry their own type (Events.md §1.4).
        performanceType: linkedEvent?.type ?? performanceType,
        eventId: linkedEventId,
        eventDay: linkedEvent ? eventDayIndex(linkedEvent, dateID) : undefined,
        createEvent:
          eventChoice.kind === "create" && eventChoice.name.trim()
            ? { name: eventChoice.name.trim(), type: performanceType, date: dateID }
            : undefined,
        trainingTypeID: selectedTrainingType?.id,
        tag: selectedTrainingType
          ? [selectedTrainingType.name]
          : typeName
            ? [typeName]
            : [],
        duration: durationMinutes,
        muscular: loadState.muscular,
        respiratory: loadState.respiratory,
        rpe,
        load,
        trainingPerformed: rpe >= 7.5 ? loadState.trainingPerformed : undefined,
        notes: loadState.notes.trim(),
        pain: loadState.pain,
        injured: loadState.injured,
        existingTrainingLogId,
        results: rows
          .filter(rowIsValid)
          .map((r) => rowToResultDraft(r))
          .filter((r): r is NonNullable<typeof r> => !!r),
      };
      if (editMode) {
        const keptIds = new Set(rows.map((r) => r.id).filter(Boolean));
        draft.removedResultIds = initialResultIdsRef.current.filter(
          (id) => !keptIds.has(id),
        );
      }
      if (
        !editMode &&
        loadState.pain &&
        loadState.niggleOpen &&
        (loadState.niggleNotes.trim() ||
          loadState.nigglePain > 1 ||
          loadState.niggleImpact > 1)
      ) {
        draft.niggle = {
          pain: loadState.nigglePain,
          impact: loadState.niggleImpact,
          notes: loadState.niggleNotes,
        };
      }

      const result = await addPerformance(uid, draft, dateID);
      await onSaved?.(result);
      handleClose();
    } catch (error) {
      console.error("Error saving performance:", error);
      setSubmitError("Could not save the performance. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActivityCreated = async (activityTypeId: string) => {
    await reloadActivities();
    // The hook state updates asynchronously; resolve from a fresh fetch.
    const types = uid ? await activityService.fetchActivityTypes(uid) : [];
    return types.find((t) => t.id === activityTypeId) ?? null;
  };

  if (!open) return null;

  const isLast = stepIndex === visibleSteps.length - 1;

  return (
    <div
      className={`fixed inset-0 ${overlayZIndexClass} bg-black/40 backdrop-blur-sm overflow-y-auto`}
    >
      <div className="flex min-h-screen md:items-center md:justify-center items-start justify-center">
        <div className="w-full max-w-2xl mx-auto overflow-hidden rounded-none md:rounded-md bg-base-100 text-left align-middle shadow-xl min-h-screen md:min-h-0 md:max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-start p-6 pb-4 flex-shrink-0">
            <div className="flex items-start gap-2">
              {stepIndex > 0 && (
                <button
                  type="button"
                  onClick={goBack}
                  aria-label="Back"
                  className="-ml-2 mt-0.5 w-8 h-8 rounded-full flex items-center justify-center text-base-content/70 hover:bg-base-200"
                >
                  <HiChevronLeft className="w-5 h-5" />
                </button>
              )}
              <div>
                <h2 className="text-lg font-grotesk">
                  {editMode ? "Edit Performance" : "Log Performance"}
                </h2>
                <div className="relative mt-0.5">
                  <button
                    type="button"
                    disabled={editMode}
                    onClick={() => {
                      const el = dateInputRef.current as
                        | (HTMLInputElement & { showPicker?: () => void })
                        | null;
                      if (!el) return;
                      if (typeof el.showPicker === "function") el.showPicker();
                      else {
                        el.focus();
                        el.click();
                      }
                    }}
                    className="flex items-center gap-1.5 text-sm text-base-content/60 hover:text-base-content transition-colors disabled:hover:text-base-content/60"
                  >
                    <span
                      className={`font-medium ${
                        editMode ? "" : "underline decoration-dotted underline-offset-4"
                      }`}
                    >
                      {formatDateID(dateID)}
                    </span>
                  </button>
                  <input
                    ref={dateInputRef}
                    type="date"
                    value={dateIDToInputValue(dateID)}
                    onChange={(e) => {
                      const next = inputValueToDateID(e.target.value);
                      if (next) setDateID(next);
                    }}
                    className="absolute left-0 top-6 w-0 h-0 opacity-0 pointer-events-none"
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="bg-base-200 p-2 rounded-full"
              aria-label="Close"
            >
              <HiX className="w-5 h-5 text-base-content/80" />
            </button>
          </div>

          {/* Progress dots */}
          <div className="px-6 pb-4 flex items-center gap-2 flex-shrink-0">
            {visibleSteps.map((s, i) => (
              <button
                key={s}
                type="button"
                aria-label={`Step ${i + 1}`}
                aria-current={i === stepIndex ? "step" : undefined}
                onClick={() => i < stepIndex && setStep(s)}
                className={`h-1.5 rounded-full transition-all ${
                  i === stepIndex
                    ? "w-6 bg-neutral"
                    : i < stepIndex
                      ? "w-1.5 bg-neutral/60"
                      : "w-1.5 bg-base-300"
                }`}
              />
            ))}
            <span className="ml-auto text-xs text-base-content/50 font-grotesk">
              {stepIndex + 1} / {visibleSteps.length}
            </span>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 pb-4">
            {step === "intent" && (
              <IntentStep
                value={performanceType}
                onSelect={(type) => {
                  setPerformanceType(type);
                  if (type !== "testing") setSelectedTrainingTypeId(null);
                }}
              />
            )}
            {step === "link" && (
              <LinkStep
                events={spanningEvents}
                pickerEvents={pickerEvents}
                trainingLogs={dayTrainingLogs}
                trainingLogLabel={trainingLogLabel}
                eventChoice={eventChoice}
                pickerOpen={pickerOpen}
                createOpen={createOpen}
                performanceType={performanceType}
                dateID={dateID}
                upgradeLogId={upgradeLogId}
                createName={createName}
                onChooseEvent={(choice) => {
                  if (choice.kind === "none") setCreateOpen(false);
                  applyEventChoice(choice);
                }}
                onTogglePicker={() => {
                  setPickerOpen((v) => !v);
                  setCreateOpen(false);
                }}
                onToggleCreate={() => {
                  const next = !createOpen;
                  setCreateOpen(next);
                  if (next) {
                    setPickerOpen(false);
                    setEventChoice({ kind: "none" });
                    setRows((prev) => prev.filter((r) => !r.targets));
                  }
                }}
                onCreateNameChange={setCreateName}
                onChooseUpgrade={applyUpgrade}
              />
            )}
            {step === "results" && performanceType && (
              <>
                {loadingSeed && (
                  <div className="flex items-center gap-2 text-sm text-base-content/60 mb-3">
                    <span className="loading loading-spinner loading-xs" />
                    Loading results...
                  </div>
                )}
                <ResultsStep
                  performanceType={performanceType}
                  rows={rows}
                  activityTypes={activityTypes}
                  loadingActivityTypes={loadingActivityTypes}
                  onRowsChange={setRows}
                  onActivityCreated={handleActivityCreated}
                  notes={loadState.notes}
                  onNotesChange={(notes) =>
                    setLoadState((prev) => ({ ...prev, notes }))
                  }
                  isPremium={isPremium}
                  pastPerformanceLogs={pastPerformanceLogs}
                  historyReady={historyReady}
                  historyLoadFailed={historyLoadFailed}
                />
              </>
            )}
            {step === "load" && performanceType && (
              <LoadStep
                performanceType={performanceType}
                state={loadState}
                onChange={(next) => {
                  setLoadState(next);
                  if (durationError && next.duration !== loadState.duration) {
                    setDurationError("");
                  }
                }}
                durationError={durationError}
                onDurationBlur={validateDuration}
                fixedTrainingType={fixedTrainingType}
                trainingTypes={trainingTypes}
                sports={sports}
                sportFilter={sportFilter}
                onSportFilterChange={setSportFilter}
                selectedTrainingTypeId={selectedTrainingTypeId}
                onTrainingTypeChange={setSelectedTrainingTypeId}
                rpe={rpe}
                load={load}
                allowNiggle={!editMode}
              />
            )}
            {step === "complete" && performanceType && (
              <CompleteStep
                performanceType={performanceType}
                eventName={eventName || null}
                trainingTypeName={selectedTrainingType?.name ?? null}
                durationMinutes={durationMinutes}
                rpe={rpe}
                load={load}
                rows={rows}
                notes={loadState.notes}
                hasNiggle={
                  !editMode &&
                  loadState.pain &&
                  loadState.niggleOpen &&
                  (loadState.niggleNotes.trim() !== "" ||
                    loadState.nigglePain > 1 ||
                    loadState.niggleImpact > 1)
                }
                editMode={editMode}
              />
            )}
            {submitError && (
              <p className="text-sm text-error font-grotesk mt-4">{submitError}</p>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 pb-5 pt-28 -mt-24 flex-shrink-0 relative z-50 flex items-center justify-between gap-3 bg-gradient-to-t from-base-100 from-30% to-transparent pointer-events-none">
            <div className="flex items-center pointer-events-auto">
              <button
                type="button"
                onClick={() => !isSubmitting && handleClose()}
                disabled={isSubmitting}
                className="ml-2 px-3 py-2 rounded-md text-sm font-medium text-base-content/45 hover:text-base-content/70 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
            {isLast ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || !performanceType}
                aria-label={editMode ? "Save changes" : "Save performance"}
                title={editMode ? "Save changes" : "Save performance"}
                className="pointer-events-auto w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center shadow-lg transition-opacity disabled:opacity-40"
              >
                {isSubmitting ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : (
                  <HiCheck className="w-6 h-6" />
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={goNext}
                disabled={!canAdvance}
                aria-label="Next"
                title="Next"
                className="pointer-events-auto w-14 h-14 rounded-full bg-neutral text-neutral-content flex items-center justify-center shadow-lg transition-opacity disabled:opacity-40"
              >
                <HiArrowRight className="w-6 h-6" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceFlow;
